import { ButtonHTMLAttributes, forwardRef } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface PrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingText?: string;
}

export const PrimaryButton = forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  ({ children, loading, loadingText = "Aguarde...", className, disabled, ...props }, ref) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          "w-full h-[48px] sm:h-[52px] lg:h-[48px] rounded-[14px] font-medium text-sm sm:text-base transition-all duration-200",
          "text-white shadow-button",
          "focus:outline-none focus:ring-[4px] focus:ring-[rgba(31,107,255,0.18)]",
          "disabled:cursor-not-allowed",
          className
        )}
        style={{
          backgroundColor: isDisabled
            ? 'hsl(217 100% 56%)'
            : 'hsl(217 100% 56%)', // #1F6BFF
          opacity: isDisabled ? 0.4 : 1,
          transform: 'translateY(0)',
          boxShadow: isDisabled
            ? 'none'
            : '0 4px 14px -2px rgba(31, 107, 255, 0.4)',
        }}
        onMouseEnter={(e) => {
          if (!isDisabled) {
            const btn = e.target as HTMLButtonElement;
            btn.style.backgroundColor = 'hsl(217 100% 49%)'; // #1A5AE0
            btn.style.transform = 'translateY(-1px)';
            btn.style.boxShadow = '0 6px 20px -2px rgba(31, 107, 255, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDisabled) {
            const btn = e.target as HTMLButtonElement;
            btn.style.backgroundColor = 'hsl(217 100% 56%)'; // #1F6BFF
            btn.style.transform = 'translateY(0)';
            btn.style.boxShadow = '0 4px 14px -2px rgba(31, 107, 255, 0.4)';
          }
        }}
        onMouseDown={(e) => {
          if (!isDisabled) {
            const btn = e.target as HTMLButtonElement;
            btn.style.backgroundColor = 'hsl(217 100% 42%)'; // #154BBD
            btn.style.transform = 'translateY(1px)';
            btn.style.boxShadow = '0 2px 8px -2px rgba(31, 107, 255, 0.3)';
          }
        }}
        onMouseUp={(e) => {
          if (!isDisabled) {
            const btn = e.target as HTMLButtonElement;
            btn.style.backgroundColor = 'hsl(217 100% 49%)'; // #1A5AE0
            btn.style.transform = 'translateY(-1px)';
            btn.style.boxShadow = '0 6px 20px -2px rgba(31, 107, 255, 0.5)';
          }
        }}
        {...props}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {loadingText}
          </span>
        ) : (
          children
        )}
      </button>
    );
  }
);

PrimaryButton.displayName = "PrimaryButton";
